import { computed, makeObservable } from 'mobx';
import { isArray } from '@cutwise/utils';

import { assignModel } from '@/common/helpers/assignModel';
import { MediaSubFile, MediaSubFileModel } from '@/media/entity/MediaSubFile';

import { mediaSubFormatRepository } from '../repository/MediaSubFormatRepository';
import { setupPresetRepository } from '../repository/SetupPresetRepository';

import Media from './Media';
import {
  IMAGE_SUB_FORMAT_BY_SIZE,
  ImageSubFormat,
  MediaSubFormatId,
  VIDEO_SUB_FORMAT_FSET_BY_SIZE,
  VideoSubFormat,
} from './MediaSubFormat';

class MediaSourceModel {
  id!: string;
  mediaSubFiles!: MediaSubFileModel[];
  scaleRatio!: number;
  bgColor!: string;
  stacked!: boolean;
  sourceExternalUrl?: string;
  hdrMode?: string;
  stereoAngle?: number;
  isRender?: Nullable<boolean>;
  plotting?: unknown;
}

export default class MediaSource extends MediaSourceModel {
  media: Media;

  constructor(model: MediaSourceModel, media: Media) {
    super();

    makeObservable(this, {
      media: false,
      getRelScale: false,
      getSubfileByMediaSize: false,
      getMaxSubFileByMediaSize: false,
      findSubFile: false,
      isEmpty: computed,
      bgColorOverrided: computed,
      relScale: computed,
      maxSubFileVideo: computed,
    });

    assignModel<MediaSourceModel>(this, model);

    this.mediaSubFiles?.forEach((msf) => {
      const mediaSubFormat = mediaSubFormatRepository.findMediaSubFormatById(
        msf.mediaSubFormat as unknown as MediaSubFormatId,
      );

      if (mediaSubFormat) {
        msf.mediaSubFormat = mediaSubFormat;
      }
    });

    this.media = media;
  }

  get isEmpty() {
    return !this.mediaSubFiles || this.mediaSubFiles.length === 0;
  }

  get bgColorOverrided() {
    if (this.media?.setupPreset?.isStandard === false) {
      return this.bgColor;
    }

    const bg = setupPresetRepository.findSetupPresetBackground(this.media.setupPreset);

    if (!bg) {
      return this.bgColor;
    }

    return bg;
  }

  get relScale() {
    const msf = this.media.isVideo ? this.findSubFile(VideoSubFormat.Fset160) : this.findSubFile(ImageSubFormat.F160);

    return this.getRelScale(msf!);
  }

  get maxSubFileVideo() {
    if (!this.media.isVideo) {
      return null;
    }

    if (!isArray(this.mediaSubFiles) || this.mediaSubFiles.length === 0) {
      return null;
    }

    return this.mediaSubFiles.slice().sort((a, b) => b.resolutionX - a.resolutionX)[0];
  }

  getRelScale(msf: MediaSubFile) {
    return this.scaleRatio / msf.resolutionY;
  }

  getSubfileByMediaSize(mediaSize: number) {
    /** @type {?number} */
    let subformat = null;

    if (this.media.isImage) {
      subformat = IMAGE_SUB_FORMAT_BY_SIZE[mediaSize];
    }

    if (this.media.isVideo) {
      subformat = VIDEO_SUB_FORMAT_FSET_BY_SIZE[mediaSize];
    }

    if (!subformat) {
      return null;
    }

    return this.findSubFile(subformat);
  }

  getMaxSubFileByMediaSize(mediaSize: number) {
    const sizesMap = this.media.isImage ? IMAGE_SUB_FORMAT_BY_SIZE : VIDEO_SUB_FORMAT_FSET_BY_SIZE;
    const possibleSizes = Object.keys(sizesMap).map((size) => +size);

    let minSize = possibleSizes[0];

    for (let i = 1; i < possibleSizes.length; i++) {
      const isSubFileExist = Boolean(this.findSubFile(sizesMap[possibleSizes[i]]));

      if (isSubFileExist && minSize < possibleSizes[i] && possibleSizes[i] <= mediaSize) {
        minSize = possibleSizes[i];
      }
    }

    const subFormat = sizesMap[minSize];

    if (!subFormat) {
      return null;
    }

    return this.findSubFile(subFormat);
  }

  findSubFile(mediaSubFormat: MediaSubFormatId) {
    return this.mediaSubFiles.find((m) => m.mediaSubFormat?.id === mediaSubFormat);
  }
}
